"use client";

import { motion, useTransform } from "motion/react";
import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { axisLabelMaxWidth } from "../axis-label-budget";
import { useChartProjection } from "../projection/chart-projection-context";
import {
  type ProjectedAxisLabel,
  ProjectedAxisLabels,
} from "../projection/projected-axis-labels";
import { useProjectionProgress } from "../projection/use-projected-path";
import { useHeatmap, useHeatmapInteractionOptional } from "./heatmap-context";
import { HEATMAP_AXIS_FADE } from "./heatmap-projection";
import {
  formatHeatmapMonthLabel,
  getHeatmapColumnMonthAnchor,
} from "./heatmap-utils";

/** Narrowest a column label box may get before labels are thinned out. */
const MIN_LABEL_SLOT = 44;
const MIN_ANGLED_LABEL_SLOT = 14;
const LABEL_GUTTER = 6;
const ANGLED_LABEL_OFFSET = 10;

export interface HeatmapXAxisProps {
  /** Additional class name for labels */
  className?: string;
  /**
   * Column names, one per grid column. Replaces the month anchors for
   * categorical grids; labels are thinned out when columns get narrow.
   */
  labels?: readonly string[];
  /**
   * Rotates category labels by this many degrees (e.g. `-45`), anchored at the
   * column centre just above the grid. Default: 0 (horizontal).
   */
  labelAngle?: number;
  /** Emphasises the label of the hovered cell's column. Default: false */
  highlightHovered?: boolean;
}

export const HeatmapXAxis = memo(function HeatmapXAxis({
  className,
  labels,
  labelAngle = 0,
  highlightHovered = false,
}: HeatmapXAxisProps) {
  const { containerRef, data, margin, binWidth, gap, innerHeight, xScale } =
    useHeatmap();
  const interaction = useHeatmapInteractionOptional();
  const { intlLocale } = useViewerLanguage();
  const hoveredColumn = highlightHovered
    ? (interaction?.hoveredCell?.column ?? null)
    : null;
  const angled = labelAngle !== 0;
  const [mounted, setMounted] = useState(false);
  const projection = useChartProjection();
  const progress = useProjectionProgress(projection);
  const fadeOpacity = useTransform(progress, HEATMAP_AXIS_FADE, [1, 0]);
  const opacity = projection ? fadeOpacity : undefined;

  useEffect(() => {
    setMounted(true);
  }, []);

  const stride = Math.max(
    1,
    Math.ceil(
      (angled ? MIN_ANGLED_LABEL_SLOT : MIN_LABEL_SLOT) / Math.max(binWidth, 1)
    )
  );

  const categoryLabels = useMemo(() => {
    if (!labels) {
      return null;
    }

    const slotWidth = stride * binWidth;
    return labels
      .slice(0, data.length)
      .map((label, columnIndex) => ({
        key: `${columnIndex}-${label}`,
        column: columnIndex,
        label,
        left: Math.max(
          margin.left,
          margin.left + xScale(columnIndex) + binWidth / 2 - slotWidth / 2
        ),
        width: slotWidth,
      }))
      .filter((_, columnIndex) => columnIndex % stride === 0);
  }, [binWidth, data.length, labels, margin.left, stride, xScale]);

  const monthLabels = useMemo(() => {
    if (labels) {
      return [];
    }

    const ticks: { label: string; x: number; key: string }[] = [];
    let lastMonthKey = "";

    for (let columnIndex = 0; columnIndex < data.length; columnIndex++) {
      const column = data[columnIndex];
      if (!column) {
        continue;
      }

      const monthAnchor = getHeatmapColumnMonthAnchor(column);
      if (!monthAnchor) {
        continue;
      }

      const monthKey = `${monthAnchor.getFullYear()}-${monthAnchor.getMonth()}`;
      if (monthKey === lastMonthKey) {
        continue;
      }

      ticks.push({
        label: formatHeatmapMonthLabel(monthAnchor, intlLocale),
        x: margin.left + xScale(columnIndex),
        key: monthKey,
      });
      lastMonthKey = monthKey;
    }

    return ticks;
  }, [data, labels, margin.left, xScale, intlLocale]);

  const projectedLabels = useMemo<ProjectedAxisLabel[]>(() => {
    if (!projection) {
      return [];
    }
    if (labels) {
      return labels.slice(0, data.length).map((label, columnIndex) => ({
        key: `${columnIndex}-${label}`,
        text: label,
        x: xScale(columnIndex) + binWidth / 2,
        y: innerHeight,
        z: 0,
      }));
    }
    return monthLabels.map((tick) => ({
      key: tick.key,
      text: tick.label,
      x: tick.x - margin.left + binWidth / 2,
      y: innerHeight,
      z: 0,
    }));
  }, [
    binWidth,
    data.length,
    innerHeight,
    labels,
    margin.left,
    monthLabels,
    projection,
    xScale,
  ]);

  const projected = projection ? (
    <ProjectedAxisLabels axis="x" labels={projectedLabels} />
  ) : null;

  const container = containerRef.current;
  if (!(mounted && container)) {
    return projected;
  }

  if (categoryLabels && angled) {
    const rise = Math.max(0, margin.top - ANGLED_LABEL_OFFSET);
    const sin = Math.abs(Math.sin((labelAngle * Math.PI) / 180));
    const maxWidth = sin > 0 ? rise / sin : undefined;

    return (
      <>
        {projected}
        {createPortal(
          categoryLabels.map((tick) => {
            const isHovered = hoveredColumn === tick.column;
            return (
              <motion.div
                className="pointer-events-none absolute"
                key={tick.key}
                style={{
                  top: rise,
                  left:
                    margin.left +
                    xScale(tick.column) +
                    (binWidth - gap) / 2,
                  width: 0,
                  height: 0,
                  opacity,
                }}
              >
                <span
                  className={cn(
                    "absolute bottom-0 left-0 origin-bottom-left truncate whitespace-nowrap text-xs leading-none transition-colors",
                    isHovered
                      ? "font-medium text-foreground"
                      : "text-chart-label",
                    className
                  )}
                  style={{ maxWidth, transform: `rotate(${labelAngle}deg)` }}
                >
                  {tick.label}
                </span>
              </motion.div>
            );
          }),
          container
        )}
      </>
    );
  }

  if (categoryLabels) {
    const maxWidth = axisLabelMaxWidth(
      "xAxisTick",
      Math.max(0, stride * binWidth - LABEL_GUTTER)
    );

    return (
      <>
        {projected}
        {createPortal(
          categoryLabels.map((tick) => (
            <motion.div
              className="pointer-events-none absolute flex justify-center"
              key={tick.key}
              style={{ top: 0, left: tick.left, width: tick.width, opacity }}
            >
              <span
                className={cn(
                  "line-clamp-2 text-center text-xs leading-tight [overflow-wrap:anywhere]",
                  hoveredColumn === tick.column
                    ? "font-medium text-foreground"
                    : "text-chart-label",
                  className
                )}
                style={{ maxWidth }}
              >
                {tick.label}
              </span>
            </motion.div>
          )),
          container
        )}
      </>
    );
  }

  return (
    <>
      {projected}
      {createPortal(
        monthLabels.map((tick) => (
          <motion.div
            className="pointer-events-none absolute"
            key={tick.key}
            style={{
              top: 0,
              left: tick.x,
              width: 0,
              display: "flex",
              justifyContent: "flex-start",
              opacity,
            }}
          >
            <span
              className={cn(
                "whitespace-nowrap text-chart-label text-xs",
                className
              )}
            >
              {tick.label}
            </span>
          </motion.div>
        )),
        container
      )}
    </>
  );
});

HeatmapXAxis.displayName = "HeatmapXAxis";

export default HeatmapXAxis;
